/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type {
  ContactFilterOptions,
  ContactsStats,
  FetchContactsResponse,
  FetchSuggestionsResponse,
} from '../../@types/contact';
import type { XHubChatClient } from '../../client';
import type { Room } from '../room/room';
import { ContactRelationship } from '../../@types/contact';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { Method } from '../../http';
import { logger } from '../../utils/logger';
import { Contact, ContactEvent } from './contact';
import { User } from './user';

/**
 * Events emitted by ContactsManager
 */
export enum ContactsEvent {
  /** Emitted when the contacts list is updated */
  Update = 'Contacts.update',
  /** Emitted when a new contact is added */
  Add = 'Contacts.add',
  /** Emitted when a contact is removed */
  Remove = 'Contacts.remove',
  /** Emitted when suggested contacts are updated */
  SuggestionsUpdate = 'Contacts.suggestionsUpdate',
  /** Emitted when any contact presence changes */
  PresenceChange = 'Contacts.presenceChange',
}

export type ContactsEventHandlerMap = {
  [ContactsEvent.Update]: (contacts: Contact[]) => void;
  [ContactsEvent.Add]: (contact: Contact) => void;
  [ContactsEvent.Remove]: (userId: string) => void;
  [ContactsEvent.SuggestionsUpdate]: (suggestions: Contact[]) => void;
  [ContactsEvent.PresenceChange]: (userId: string, presence: string) => void;
};

/**
 * Options for ContactsManager
 */
export type ContactsManagerOptions = {
  /** Maximum number of contacts to track */
  maxContacts?: number;
  /** Whether to automatically fetch suggestions */
  autoFetchSuggestions?: boolean;
  /** Interval to refresh suggestions (ms) */
  suggestionRefreshInterval?: number;
  /** Whether to persist contacts to storage */
  persistToStorage?: boolean;
};

/**
 * ContactsManager manages the user's contact list, including suggested contacts
 * and direct messaging functionality.
 *
 * @example
 * ```typescript
 * const contactsManager = client.getContactsManager();
 * await contactsManager.initialize();
 *
 * // Listen for updates
 * contactsManager.on(ContactsEvent.Update, (contacts) => {
 *   console.log('Contacts updated:', contacts.length);
 * });
 *
 * // Get contacts
 * const contacts = contactsManager.getContacts();
 * const onlineContacts = contactsManager.getOnlineContacts();
 *
 * // Add contact
 * const contact = await contactsManager.addContact('user123');
 *
 * // Create DM
 * const room = await contactsManager.createDirectMessage('user123');
 * ```
 */
export class ContactsManager extends TypedEventEmitter<ContactsEvent, ContactsEventHandlerMap> {
  public contacts = new Map<string, Contact>();
  private suggestedContacts = new Map<string, Contact>();
  private initialized = false;
  private suggestionRefreshTimer?: ReturnType<typeof setInterval>;

  private readonly options: Required<ContactsManagerOptions> = {
    maxContacts: 1000,
    autoFetchSuggestions: false,
    suggestionRefreshInterval: 5 * 60 * 1000, // 5 minutes
    persistToStorage: true,
  };

  public constructor(
    private readonly client: XHubChatClient,
    options: ContactsManagerOptions = {},
  ) {
    super();
    this.options = { ...this.options, ...options };
  }

  /**
   * Initialize contacts manager.
   * Loads contacts from storage and fetches from server.
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      logger.warn('ContactsManager already initialized');
      return;
    }

    try {
      logger.debug('Initializing ContactsManager...');

      // Load from storage first for quick access
      if (this.options.persistToStorage) {
        await this.loadFromStorage();
      }

      // Fetch fresh data from server
      this.fetchContacts();

      // Fetch suggestions if enabled
      if (this.options.autoFetchSuggestions) {
        await this.fetchSuggestions();
        this.startSuggestionRefresh();
      }

      this.initialized = true;
      logger.debug('ContactsManager initialized', {
        contactsCount: this.contacts.size,
        suggestionsCount: this.suggestedContacts.size,
      });
    } catch (error) {
      logger.error('Failed to initialize ContactsManager', error);
      throw error;
    }
  }

  /**
   * Fetch contacts from server
   *
   * @param cursor - Pagination cursor
   * @returns List of fetched contacts
   */
  public async fetchContacts(cursor?: string): Promise<Contact[]> {
    try {
      logger.debug('Fetching contacts from server', { cursor });

      const response = await this.client.http.authedRequest<FetchContactsResponse>(
        Method.Get,
        '/contacts',
        cursor ? { cursor } : undefined,
      );

      const contacts: Contact[] = [];
      for (const data of response.data) {
        const user = this.client.getUser(data.channel_slug ?? data.id) ?? User.createUser(data.id, data.channel_slug, this.client);

        if (!user) {
          logger.warn(`User ${data.id} not found, skipping`);
          continue;
        }

        user.setAvatarUrl(data.logo);
        user.setDisplayName(data.name);
        user.setRawDisplayName(data.name);
        user.setDescription(data.description);

        const contact = new Contact(data.id, user, ContactRelationship.Added, {}, this.client);

        this.contacts.set(data.channel_slug, contact);
        contacts.push(contact);

        // Listen to contact events
        this.setupContactListeners(contact);
      }

      if (this.options.persistToStorage) {
        await this.saveToStorage();
      }

      this.client.store.save(true);

      this.emit(ContactsEvent.Update, Array.from(this.contacts.values()));
      logger.debug(`Fetched ${contacts.length} contacts`);
      return contacts;
    } catch (error) {
      logger.error('Failed to fetch contacts', error);
      throw error;
    }
  }

  /**
   * Fetch suggested contacts from server
   *
   * @param cursor - Pagination cursor
   * @returns List of suggested contacts
   */
  public async fetchSuggestions(cursor?: string): Promise<Contact[]> {
    try {
      logger.debug('Fetching contact suggestions', { cursor });

      const response = await this.client.http.authedRequest<FetchSuggestionsResponse>(
        Method.Get,
        '/contacts/suggestions',
        cursor ? { cursor } : undefined,
      );

      const suggestions: Contact[] = [];
      for (const data of response.suggestions) {
        // Skip if already in contacts
        if (this.contacts.has(data.userId)) {
          continue;
        }

        const user = this.client.getUser(data.userId);
        if (!user) {
          logger.warn(`User ${data.userId} not found, skipping suggestion`);
          continue;
        }

        // Update user info if provided
        if (data.displayName) user.setDisplayName(data.displayName);
        if (data.avatarUrl) user.setAvatarUrl(data.avatarUrl);

        const contact = new Contact(data.userId, user, ContactRelationship.Suggested, data.metadata, this.client);

        this.suggestedContacts.set(data.userId, contact);
        suggestions.push(contact);
      }

      this.emit(ContactsEvent.SuggestionsUpdate, suggestions);
      logger.debug(`Fetched ${suggestions.length} suggestions`);
      return suggestions;
    } catch (error) {
      logger.error('Failed to fetch suggestions', error);
      throw error;
    }
  }

  /**
   * Remove a contact
   *
   * @param userId - User ID to remove
   */
  public async removeContact(userId: string): Promise<void> {
    try {
      logger.info(`Removing contact ${userId}`);

      await this.client.http.authedRequest(
        Method.Delete,
        `/contacts/${encodeURIComponent(userId)}`,
      );

      const contact = this.contacts.get(userId);
      if (contact) {
        contact.destroy();
      }

      this.contacts.delete(userId);

      if (this.options.persistToStorage) {
        await this.saveToStorage();
      }

      this.emit(ContactsEvent.Remove, userId);
      this.emit(ContactsEvent.Update, Array.from(this.contacts.values()));

      logger.info(`Contact ${userId} removed successfully`);
    } catch (error) {
      logger.error(`Failed to remove contact ${userId}`, error);
      throw error;
    }
  }

  /**
   * Add a contact
   *
   * @param userId - User ID to add
   * @param metadata - Optional metadata for the contact
   * @returns The added contact
   */
  public async addContact(userId: string, metadata: Record<string, any> = {}): Promise<Contact> {
    try {
      logger.info(`Adding contact ${userId}`);

      // Check if user exists
      const user = this.client.getUser(userId);
      if (!user) {
        throw new Error(`User ${userId} not found`);
      }

      // Call API to add contact
      await this.client.http.authedRequest(
        Method.Post,
        '/contacts',
        undefined,
        { userId, metadata },
      );

      // Create contact object
      const contact = new Contact(userId, user, ContactRelationship.Added, metadata, this.client);

      // Add to contacts map
      this.contacts.set(userId, contact);

      // Setup event listeners
      this.setupContactListeners(contact);

      // Save to storage if enabled
      if (this.options.persistToStorage) {
        await this.saveToStorage();
      }

      // Emit events
      this.emit(ContactsEvent.Add, contact);
      this.emit(ContactsEvent.Update, Array.from(this.contacts.values()));

      logger.info(`Contact ${userId} added successfully`);
      return contact;
    } catch (error) {
      logger.error(`Failed to add contact ${userId}`, error);
      throw error;
    }
  }

  /**
   * Get all contacts
   */
  public getContacts(): Contact[] {
    return Array.from(this.contacts.values());
  }

  /**
   * Get suggested contacts
   */
  public getSuggestedContacts(): Contact[] {
    return Array.from(this.suggestedContacts.values());
  }

  /**
   * Get a specific contact
   *
   * @param userId - User ID
   * @returns Contact if found, undefined otherwise
   */
  public getContact(userId: string): Contact | undefined {
    return this.contacts.get(userId);
  }

  /**
   * Search contacts by query
   *
   * @param query - Search query (matches display name or user ID)
   * @returns Matching contacts
   */
  public searchContacts(query: string): Contact[] {
    const lowerQuery = query.toLowerCase();
    return this.getContacts().filter(contact =>
      contact.displayName?.toLowerCase().includes(lowerQuery)
      || contact.userId.toLowerCase().includes(lowerQuery),
    );
  }

  /**
   * Filter contacts based on criteria
   *
   * @param options - Filter options
   * @returns Filtered contacts
   */
  public filterContacts(options: ContactFilterOptions): Contact[] {
    let contacts = this.getContacts();

    // Filter by relationship
    if (options.relationship) {
      contacts = contacts.filter(c => c.relationship === options.relationship);
    }

    // Filter by online status
    if (options.onlineOnly) {
      contacts = contacts.filter(c => c.isOnline());
    }

    // Filter by tags
    if (options.tags && options.tags.length > 0) {
      contacts = contacts.filter(c =>
        options.tags!.some(tag => c.hasTag(tag)),
      );
    }

    // Filter by search query
    if (options.query) {
      const lowerQuery = options.query.toLowerCase();
      contacts = contacts.filter(c =>
        c.displayName?.toLowerCase().includes(lowerQuery)
        || c.userId.toLowerCase().includes(lowerQuery),
      );
    }

    // Sort
    if (options.sortBy) {
      contacts.sort((a, b) => {
        let valueA: any;
        let valueB: any;

        switch (options.sortBy) {
          case 'displayName':
            valueA = a.displayName?.toLowerCase();
            valueB = b.displayName?.toLowerCase();
            break;
          case 'lastInteraction':
            valueA = a.lastInteraction || 0;
            valueB = b.lastInteraction || 0;
            break;
          case 'addedAt':
            valueA = a.addedAt;
            valueB = b.addedAt;
            break;
          default:
            return 0;
        }

        const direction = options.sortDirection === 'desc' ? -1 : 1;
        return valueA < valueB ? -direction : valueA > valueB ? direction : 0;
      });
    }

    return contacts;
  }

  /**
   * Get online contacts
   */
  public getOnlineContacts(): Contact[] {
    return this.getContacts().filter(contact => contact.isOnline());
  }

  /**
   * Get recently active contacts (within last 10 minutes)
   */
  public getRecentlyActiveContacts(): Contact[] {
    return this.getContacts().filter(contact => contact.isRecentlyActive());
  }

  /**
   * Get contacts statistics
   */
  public getStats(): ContactsStats {
    const contacts = this.getContacts();
    const stats: ContactsStats = {
      total: contacts.length,
      online: this.getOnlineContacts().length,
      recentlyActive: this.getRecentlyActiveContacts().length,
      byRelationship: {
        [ContactRelationship.Suggested]: 0,
        [ContactRelationship.Added]: 0,
        [ContactRelationship.Mutual]: 0,
        [ContactRelationship.Blocked]: 0,
      },
    };

    contacts.forEach((contact) => {
      stats.byRelationship[contact.relationship]++;
    });

    return stats;
  }

  /**
   * Create direct message with a contact
   *
   * @param channel_slug - User ID to create DM with
   * @returns The room for direct messaging
   */
  public async createDirectMessage(channel_slug: string): Promise<Room> {
    const contact = this.contacts.get(channel_slug);
    if (contact) {
      const room = await contact.getOrCreateDirectRoom();
      contact.updateLastInteraction();
      return room;
    }

    // If not in contacts, create DM directly via client
    logger.info(`Creating direct message with non-contact user ${channel_slug}`);
    return this.client.createDirectRoom(channel_slug);
  }

  /**
   * Setup event listeners for a contact
   */
  private setupContactListeners(contact: Contact): void {
    contact.on(ContactEvent.Update, () => {
      if (this.options.persistToStorage) {
        this.saveToStorage().catch(err =>
          logger.error('Failed to save contacts after update', err));
      }
      this.emit(ContactsEvent.Update, Array.from(this.contacts.values()));
    });

    contact.on(ContactEvent.PresenceChange, (presence: string) => {
      this.emit(ContactsEvent.PresenceChange, contact.userId, presence);
    });
  }

  /**
   * Start auto-refresh for suggestions
   */
  private startSuggestionRefresh(): void {
    if (this.suggestionRefreshTimer) return;

    logger.debug('Starting suggestion auto-refresh', {
      interval: this.options.suggestionRefreshInterval,
    });

    this.suggestionRefreshTimer = setInterval(() => {
      this.fetchSuggestions().catch(error =>
        logger.error('Failed to refresh suggestions', error));
    }, this.options.suggestionRefreshInterval);
  }

  /**
   * Stop auto-refresh
   */
  public stopSuggestionRefresh(): void {
    if (this.suggestionRefreshTimer) {
      clearInterval(this.suggestionRefreshTimer);
      this.suggestionRefreshTimer = undefined;
      logger.debug('Stopped suggestion auto-refresh');
    }
  }

  /**
   * Save contacts to storage
   * Contacts are stored as users in the 'users' store
   */
  private async saveToStorage(): Promise<void> {
    try {
      // Store each contact's user object in the users store
      for (const contact of this.contacts.values()) {
        const user = contact.getUser();
        if (user) {
          this.client.store.storeUser(user);
        }
      }
      logger.debug(`Saved ${this.contacts.size} contacts to storage`);
    } catch (error) {
      logger.error('Failed to save contacts to storage', error);
    }
  }

  /**
   * Load contacts from storage
   * Contacts are loaded from users in the 'users' store
   */
  private async loadFromStorage(): Promise<Contact[]> {
    try {
      const users = this.client.store.getUsers();
      const contacts: Contact[] = [];

      for (const user of users) {
        const contact = new Contact(user.id, user, ContactRelationship.Added, {}, this.client);

        this.contacts.set(user.id, contact);
        contacts.push(contact);

        // Listen to contact events
        this.setupContactListeners(contact);
      }

      if (this.options.persistToStorage) {
        await this.saveToStorage();
      }
      this.emit(ContactsEvent.Update, Array.from(this.contacts.values()));
      return contacts;
    } catch (error) {
      logger.error('Failed to load contacts from storage', error);
      return [];
    }
  }

  /**
   * Check if manager is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    logger.debug('Destroying ContactsManager');
    this.stopSuggestionRefresh();

    // Destroy all contacts
    this.contacts.forEach(contact => contact.destroy());
    this.suggestedContacts.forEach(contact => contact.destroy());

    this.contacts.clear();
    this.suggestedContacts.clear();
    this.removeAllListeners();
    this.initialized = false;
  }
}
